import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import {
  buildSubscriptionsView,
  type SubscriptionsView,
} from './subscriptionsModel';
import type { User } from '../dataProvider/types';

/**
 * Region-driven Plus subscriptions view, read through the aggregation seam
 * (`buildSubscriptionsView`), NOT computed in the component. For v1 it pulls the
 * raw users from the mock provider and aggregates client-side; the real-data
 * migration replaces the fetch-and-aggregate here with a single server-side
 * endpoint and the surface does not change. Same seam idiom as useStorePerformance.
 *
 * The surface is a filtered view over users, so it fetches `users` (not a new
 * resource) and the model keeps only plusStatus != none per market.
 *
 * Scope comes from the region context (UK | US | GLOBAL): the surface shows every
 * band the scope derives to at once, so it keys off `region`, not `activeRegion`.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useSubscriptions = (): {
  view: SubscriptionsView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', region],
    queryFn: async () => {
      const users = await dataProvider.getList('users', ALL);
      return buildSubscriptionsView(
        { users: users.data as unknown as User[] },
        { scope: region },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
